/**
 * ProductinformatieBlok — wat de leverancier over dit materiaal deelt.
 *
 * De Hub-sync schrijft zichtbaar gemaakte leveranciersvelden weg onder
 * `eigenschappen.orion_supplier_hub`, elk met een rol (VERKOOP of TECHNISCH). Dit blok
 * toont ze read-only: de Hub is de bron, de schrijnwerker wijzigt hier niets.
 *
 * UITSLUITEND die sleutel lezen. `eigenschappen` wordt door meerdere bronnen gevuld —
 * onder andere een derde bron die dezelfde veldnamen gebruikt (decor_naam, dikte, merk).
 * Alleen wat onder orion_supplier_hub staat komt van de leverancier.
 */

// Leesbare labels, spiegelt orion_core/services/productinformatie.py. Ontbreekt een key,
// dan wordt de sleutel opgemaakt (decor_naam -> "Decor naam") — beter ruw dan leeg.
const LABELS = {
  lengte: 'Lengte', breedte: 'Breedte', dikte: 'Dikte', hoogte: 'Hoogte', diepte: 'Diepte',
  drager: 'Drager', laminaat_type: 'Laminaattype', bekleding_top: 'Bekleding boven',
  bekleding_bottom: 'Bekleding onder', structuur: 'Structuur', decor_naam: 'Decor',
  decor_code: 'Decorcode', merk: 'Merk', gewicht_per_m2: 'Gewicht per m²',
  lichtbestendigheid: 'Lichtbestendigheid', houtsoort: 'Houtsoort', materiaal: 'Materiaal',
  materiaal_type: 'Materiaalsoort', model_naam: 'Model', model_nummer: 'Modelnummer',
  artikel_nummer: 'Artikelnummer', serie: 'Serie', kleur: 'Kleur',
  kleur_afwerking: 'Kleur/afwerking', afwerking: 'Afwerking', oppervlak: 'Oppervlak',
  energie_klasse: 'Energieklasse', vermogen: 'Vermogen', aansluiting_type: 'Aansluiting',
  apparaat_type: 'Type', sub_type: 'Subtype', beslag_type: 'Beslagtype',
  opening_hoek: 'Openingshoek', draagkracht: 'Draagkracht', demping: 'Demping',
  montage_type: 'Montage', waterbestendig: 'Waterbestendig',
  hittebestendig: 'Hittebestendig', werkblad_type: 'Werkbladtype', lijm_type: 'Lijmtype',
  rol_lengte: 'Rollengte', buigradius: 'Buigradius', collectie: 'Collectie',
}

function label(key) {
  if (LABELS[key]) return LABELS[key]
  const woord = key.replace(/_/g, ' ')
  return woord.charAt(0).toUpperCase() + woord.slice(1)
}

/** Splits het Hub-blok in verkoop/technisch. Retourneert null als er niets is. */
export function splitsProductinformatie(eigenschappen) {
  const blok = eigenschappen?.orion_supplier_hub
  if (!blok || typeof blok !== 'object') return null
  const verkoop = []
  const technisch = []
  for (const [key, gegeven] of Object.entries(blok)) {
    if (!gegeven || typeof gegeven !== 'object') continue
    const waarde = gegeven.waarde
    if (waarde === null || waarde === undefined || waarde === '') continue
    const regel = { key, label: label(key), waarde: String(waarde) }
    ;(gegeven.rol === 'VERKOOP' ? verkoop : technisch).push(regel)
  }
  if (verkoop.length === 0 && technisch.length === 0) return null
  return { verkoop, technisch }
}

export default function ProductinformatieBlok({ eigenschappen }) {
  const info = splitsProductinformatie(eigenschappen)
  // Geen leeg kader bij een eigen materiaal: wie niets van een leverancier krijgt,
  // hoort geen lege sectie te zien.
  if (!info) return null

  return (
    <div className="pt-3 border-t border-line dark:border-gray-700">
      <div className="text-xs font-semibold uppercase tracking-wide text-faint mb-2">
        Productinformatie van de leverancier
      </div>

      {info.verkoop.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-wide text-muted mb-1">
            Verkoopargumenten
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            {info.verkoop.map((v) => (
              <div key={v.key} className="contents">
                <dt className="text-xs text-muted">{v.label}</dt>
                <dd className="text-xs text-ink dark:text-white">{v.waarde}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {info.technisch.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted mb-1">
            Technische gegevens
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            {info.technisch.map((v) => (
              <div key={v.key} className="contents">
                <dt className="text-xs text-muted">{v.label}</dt>
                <dd className="text-xs text-ink dark:text-white">{v.waarde}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  )
}
